import { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, Send, CheckCircle, ClipboardList } from 'lucide-react';
import { getCart, updateQty, clearCart, itemKey, cartTotal, formatEur } from './cart';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SmartcasesCart = () => {
  const [items, setItems] = useState(getCart());
  const [form, setForm] = useState({ company: '', phone: '', email: '' });
  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  useEffect(() => {
    const sync = () => {
      const next = getCart();
      setItems(next);
      if (next.length > 0) setStatus((s) => (s.success ? { loading: false, success: false, error: null } : s));
    };
    window.addEventListener('smartcases-cart', sync);
    return () => window.removeEventListener('smartcases-cart', sync);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { setStatus({ loading: false, success: false, error: 'Der Einkaufszettel ist leer.' }); return; }
    setStatus({ loading: true, success: false, error: null });
    try {
      await axios.post(`${API}/smartcases/quote`, { ...form, items });
      setStatus({ loading: false, success: true, error: null });
      clearCart();
      setForm({ company: '', phone: '', email: '' });
    } catch (err) {
      setStatus({ loading: false, success: false, error: err.response?.data?.detail || 'Fehler beim Senden. Bitte versuche es erneut.' });
    }
  };

  return (
    <div className="card-ink card-ink--shadow p-5" data-testid="smartcases-cart">
      <h3 className="font-display font-bold text-xl flex items-center gap-2 mb-4">
        <ClipboardList className="w-5 h-5 text-purple" /> Einkaufszettel
        {items.length > 0 && <span className="text-xs bg-purple text-off px-2 py-0.5 font-sans" data-testid="cart-count">{items.reduce((s, i) => s + i.qty, 0)}</span>}
      </h3>

      {status.success && (
        <div className="border-2 border-ink bg-lilac p-4 mb-4 text-sm flex gap-2" data-testid="quote-success">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>Vielen Dank! Deine Anfrage wurde gesendet – wir melden uns in Kürze mit einem unverbindlichen Angebot.</span>
        </div>
      )}

      {items.length === 0 && !status.success && (
        <p className="text-sm text-ink/70 mb-4" data-testid="cart-empty">Noch keine Produkte ausgewählt. Füge Produkte über „Auf den Zettel" hinzu.</p>
      )}

      {items.length > 0 && (
        <div className="space-y-3 mb-4 max-h-72 overflow-y-auto pr-1">
          {items.map((item) => (
            <div key={itemKey(item)} className="flex items-start gap-2 text-sm border-b border-ink/15 pb-3" data-testid={`cart-item-${item.slug}`}>
              <div className="flex-1 min-w-0">
                <p className="font-bold leading-tight">{item.name}</p>
                {item.variant && <p className="text-xs text-ink/70">{item.variant}</p>}
                {item.payment && <p className="text-xs text-purple font-medium">{item.payment}</p>}
                <p className="text-xs text-ink/80 mt-0.5">{formatEur(item.price)} / Stück</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => updateQty(itemKey(item), item.qty - 1)} className="w-6 h-6 border-2 border-ink bg-off hover:bg-lilac font-bold leading-none" data-testid={`cart-minus-${item.slug}`}>−</button>
                <span className="w-6 text-center">{item.qty}</span>
                <button onClick={() => updateQty(itemKey(item), item.qty + 1)} className="w-6 h-6 border-2 border-ink bg-off hover:bg-lilac font-bold leading-none" data-testid={`cart-plus-${item.slug}`}>+</button>
                <button onClick={() => updateQty(itemKey(item), 0)} className="ml-1 text-ink/50 hover:text-red-700" data-testid={`cart-remove-${item.slug}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          <div className="flex justify-between font-display font-bold pt-2 border-t-2 border-ink">
            <span>Gesamt <span className="font-sans font-normal text-xs text-ink/60">(netto)</span></span>
            <span data-testid="cart-total">{formatEur(cartTotal(items))}</span>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-3">
        <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Name deiner Gastronomie *" className="field-input text-sm" data-testid="quote-company-input" />
        <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefonnummer *" className="field-input text-sm" data-testid="quote-phone-input" />
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-Mail-Adresse *" className="field-input text-sm" data-testid="quote-email-input" />
        {status.error && <p className="text-xs text-red-700 font-medium" data-testid="quote-error">{status.error}</p>}
        <button type="submit" disabled={status.loading || items.length === 0} className="btn btn--purple btn--sm btn--block" data-testid="quote-submit-button">
          <Send className="w-4 h-4" /> {status.loading ? 'Wird gesendet...' : 'Unverbindliches Angebot erhalten'}
        </button>
        <p className="text-[11px] text-ink/60 leading-snug">Unverbindlich & kostenlos. Alle Preise netto zzgl. MwSt. Wir melden uns mit einem persönlichen Angebot inkl. Beratung zur passenden Konfiguration. Optische Abweichungen möglich.</p>
      </form>
    </div>
  );
};
